import { toast } from '@heroui/react';
import { useEffect, useRef } from 'react';

import type { ToastVariant } from '@/Providers/PageContextProvider/PageContext';

export type { ToastVariant } from '@/Providers/PageContextProvider/PageContext';

// Errors usually need reading and acting on, so they outlive confirmations.
const defaultDurations: Record<ToastVariant, number> = {
  default: 4000,
  success: 4000,
  danger: 8000,
};

type Props = {
  readonly message: string;
  readonly variant?: ToastVariant;
  readonly duration?: number;
  readonly callback?: () => void;
};

/** Bridges a message string into HeroUI's toast queue; needs a mounted `Toast.Provider`. */
const Toast = ({ message, variant = 'default', duration, callback }: Props) => {
  const currentId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!message) {
      return;
    }

    const id = toast(message, {
      variant,
      timeout: duration ?? defaultDurations[variant],
      onClose: () => {
        // A newer message may already own the slot; clearing it here would swallow it.
        if (currentId.current === id) {
          callback?.();
        }
      },
    });
    // eslint-disable-next-line @silverhand/fp/no-mutation
    currentId.current = id;

    return () => {
      // eslint-disable-next-line @silverhand/fp/no-mutation
      currentId.current = undefined;
      toast.close(id);
    };
  }, [callback, duration, message, variant]);

  return null;
};

export default Toast;
